import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GalleryCarousel } from './GalleryCarousel';

const SHOP_URL = 'https://mma-boxing-gym.myshopify.com';

const NAV: ReadonlyArray<{ to: string; label: string; external?: boolean }> = [
  { to: '/', label: 'Home' },
  { to: '/programs', label: 'Programs' },
  { to: '/schedule', label: 'Schedule' },
  { to: '/location', label: 'Location' },
  { to: '/contact', label: 'Contact' },
  { to: SHOP_URL, label: 'Shop', external: true },
];

export function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  // React Router doesn't auto-scroll to #hash on client-side nav. Wire it up.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait one frame for the route's content to render before scrolling.
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.pathname, location.hash]);
  // On the home page the carousel sits below the hero (rendered inline by HomePage).
  // Everywhere else it sits below the page content, above the footer.
  const isHome = location.pathname === '/';
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      {!isHome && <GalleryCarousel />}
      <Footer />
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Close the drawer on every route change so the next page lands clean.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header className="sticky top-0 z-30 border-b border-bone/10 bg-ink/80 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-3 px-3 sm:px-4 lg:px-6">
        <Link
          to="/"
          aria-label="George Prevalsky's Boxing & Muay Thai Gym - Home"
          className="flex min-w-0 items-center gap-2 font-display tracking-widest"
        >
          <img
            src="/assets/logo.png"
            alt=""
            aria-hidden="true"
            className="h-8 w-8 shrink-0 rounded-full object-cover"
          />
          <span className="truncate text-base sm:hidden">George&apos;s Gym</span>
          <span className="hidden truncate whitespace-nowrap sm:inline sm:text-base lg:text-lg xl:text-xl">
            George Prevalsky&apos;s Boxing &amp; Muay Thai Gym
          </span>
        </Link>
        <nav className="hidden gap-7 md:flex">
          {NAV.map((n) =>
            n.external ? (
              <a
                key={n.to}
                href={n.to}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm uppercase tracking-wider text-bone/60 transition-colors hover:text-bone"
              >
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-wider transition-colors ${
                    isActive ? 'text-bone' : 'text-bone/60 hover:text-bone'
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>
        {/* Desktop CTA */}
        <Link
          to="/trial"
          className="btn-primary hidden shrink-0 whitespace-nowrap md:inline-block md:px-5 md:py-3"
        >
          Book a Trial Class
        </Link>
        {/* Mobile hamburger replaces the CTA under md. */}
        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded border border-bone/15 text-bone md:hidden"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            {open ? (
              <>
                <path d="M6 6 18 18" />
                <path d="M18 6 6 18" />
              </>
            ) : (
              <>
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </>
            )}
          </svg>
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id="mobile-nav"
            key="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-bone/10 bg-ink md:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-4">
              {NAV.map((n) =>
                n.external ? (
                  <a
                    key={n.to}
                    href={n.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded px-3 py-3 text-sm uppercase tracking-wider text-bone/80 hover:bg-bone/5"
                  >
                    {n.label}
                  </a>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === '/'}
                    className={({ isActive }) =>
                      `rounded px-3 py-3 text-sm uppercase tracking-wider hover:bg-bone/5 ${
                        isActive ? 'text-bone' : 'text-bone/70'
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                )
              )}
              <Link to="/trial" className="btn-primary mt-2 text-center">
                Book a Trial Class
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-bone/10 bg-ink py-10">
      <div className="container-x grid gap-6 text-sm text-bone/60 md:grid-cols-3">
        <div>
          <div className="font-display text-xl text-bone">
            George Prevalsky&apos;s Boxing &amp; Muay Thai Gym
          </div>
          <p className="mt-2">612 Valley Ranch Pkwy S</p>
          <p>Irving, TX 75063</p>
          <p className="mt-2">
            <a href="tel:+19729775605" className="hover:text-bone">
              (972) 977-5605
            </a>
          </p>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider text-bone">Programs</div>
          <ul className="mt-2 space-y-1">
            <li>Boxing</li>
            <li>Muay Thai</li>
            <li>MMA</li>
            <li>Brazilian Jiu-Jitsu</li>
            <li>Kids</li>
          </ul>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider text-bone">Visit</div>
          <p className="mt-2">New here? Try a class for $20 - no commitment.</p>
          <Link to="/trial" className="mt-3 inline-block text-accent hover:underline">
            Book a trial →
          </Link>
          <div className="mt-4 flex gap-3" aria-label="Social links">
            <SocialLink href="https://www.facebook.com/GeorgesMmaAndBoxingGym/" label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.13 8.44 9.94v-7.03H7.9v-2.91h2.54v-2.2c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.91h-2.33v7.03C18.34 21.19 22 17.06 22 12.06z" />
              </svg>
            </SocialLink>
            <SocialLink href="https://www.instagram.com/muaythaiboxing_dallas/" label="Instagram">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </SocialLink>
            <SocialLink
              href="https://www.yelp.com/biz/george-prevalskys-boxing-and-muay-thai-boxing-club-irving-3"
              label="Yelp"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path d="M14.4 13.7l-1.1.4c-.7.2-1.5-.3-1.5-1V4.2c0-.8.7-1.4 1.5-1.2 2 .5 3.9 1.6 5.4 3.1.5.5.5 1.4-.1 1.9l-4.2 5.7zM10 12.7c0 .7-.7 1.2-1.4 1L4.5 12.4c-.7-.2-1.1-1-.7-1.7.7-1.4 1.7-2.6 2.9-3.6.6-.5 1.5-.3 1.9.4l1.4 4.2c.1.4 0 .7 0 1zM10.7 14.6c.7-.2 1.5.3 1.5 1.1v4.5c0 .8-.8 1.4-1.6 1.1-1.4-.5-2.7-1.3-3.7-2.3-.5-.5-.5-1.4.1-1.9l3.7-2.5zM14.4 16l3.6 2.6c.6.5.6 1.4 0 1.9-1 .9-2.2 1.5-3.5 1.9-.8.3-1.6-.4-1.6-1.2v-4.4c0-.8.8-1.3 1.5-.8zM12.6 14.4c-.6-.5-.5-1.4.2-1.7l4.2-1.6c.7-.3 1.5.2 1.5 1 .1 1.5-.2 3.1-.9 4.5-.4.7-1.3.8-1.9.3l-3.1-2.5z" />
              </svg>
            </SocialLink>
          </div>
        </div>
      </div>
      <div className="container-x mt-8 text-xs text-bone/40">
        Copyright © 2026 Georges MMA and Boxing Gym - All Rights Reserved.
      </div>
    </footer>
  );
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="grid h-9 w-9 place-items-center rounded-full border border-bone/15 text-bone/60 transition-colors hover:border-accent hover:text-accent"
    >
      {children}
    </a>
  );
}
